import React, { useState, useEffect, useCallback, useRef } from 'react'
import { ResearchTopic, ViewMode, AudioState, ToastMessage, Location } from '../types'
import { LOCATIONS, saveResearchArticle, unsaveResearchArticle, fetchSavedResearch, getResearchDetails, likeResearchArticle, unlikeResearchArticle, getAuthHeader } from '../lib/api'
import * as api from '../lib/api'
import { fetchPublicResearchWithFallback } from '../lib/apiWithFallback'
import { fetchExploreCards, fetchResearchDetails as fetchFullDetails, fetchResearchReport, type ResearchCard } from '../lib/exploreApi'
import ResearchModal from '../components/ResearchModal'
import { useAuth } from '../contexts/AuthContext'
import PageHeader from '../components/PageHeader'
import { LoadingAnimation } from '../components/ui/LoadingAnimation'
import {
  getTopicPreviewText,
  resolveTopicPreviewSummary,
} from '../lib/explorePreview'
import { normalizeResearchTitle } from '../lib/utils'
import { analytics, useScreenEngagement } from '../lib/analytics'
import { CockpitRuntime } from '../components/cockpit/CockpitRuntime'
import { compileViews } from '../lib/cockpit/compiler/viewCompiler'
import { DEFAULT_PERSONAS, type CIG } from '../lib/cockpit/schemas'
import { normalizeLegacyMetrics } from '../lib/cockpit/normalizers'
import {
  buildPodcastAudioState,
  getCompactPodcastActionLabel,
  getPodcastActionState,
  resolveIntelligencePodcastPlayback,
} from '../lib/podcastPlayback'
import { hydratePodcastSurfaceState } from '../lib/podcastHydration'
import { API_BASE_URL } from '../lib/apiBaseUrl'
import { filterProductionDiscoveryItems } from '../lib/reportDiscovery'
import { resolveAssetUrl, resolveResearchCardImageUrl } from '../lib/urlFormatting'

const buildAuthorAvatarImageUrl = (authorId?: string | number | null): string | undefined => {
  if (authorId === undefined || authorId === null || authorId === '') return undefined
  return `${API_BASE_URL}/user/${authorId}/avatar/image`
}

// Interface for intelligence report cockpit data
interface IntelligenceReportData {
  cig: CIG
  sib: any
  written: any
  scenario: any
  qa: any
  plan: any
  deepResearch?: { text?: string; citations?: any[]; sources?: any[]; status?: string; provider?: string } | null
}

interface HomePageProps {
  viewMode: ViewMode
  isDarkMode: boolean
  onToggleDarkMode: () => void
  onAudioStateChange: (state: AudioState) => void
  onShowToast: (toast: Omit<ToastMessage, 'id'>) => void
  onArticleOpen?: (isOpen: boolean) => void
  onTabChange?: (tab: 'research' | 'explore' | 'saved' | 'profile') => void
  onNavigateToProfile?: (userEmail: string) => void
}

type ViewLayout = 'horizontal' | 'mosaic'

const DESKTOP_LAYOUT_STORAGE_KEY = 'explore-desktop-layout'
const MOBILE_LAYOUT_STORAGE_KEY = 'explore-mobile-feed-layout'
const PREVIEW_MAX_LENGTH = 360

// Beautiful high-quality contextual images


export default function HomePage({ viewMode, isDarkMode, onToggleDarkMode, onAudioStateChange, onShowToast, onArticleOpen, onTabChange, onNavigateToProfile }: HomePageProps) {
  // Screen engagement tracking
  const engagementTracking = useScreenEngagement({ screenName: 'explore' })
  const trackedTopicViewIdsRef = useRef<Set<string>>(new Set())
  
  const [topics, setTopics] = useState<ResearchTopic[]>([])
  const [savedTopicIds, setSavedTopicIds] = useState<Set<string>>(new Set())
  const [likedTopicIds, setLikedTopicIds] = useState<Set<string>>(new Set())
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({})
  const [pendingLikes, setPendingLikes] = useState<Set<string>>(new Set())
  const [currentLocation] = useState<Location>(LOCATIONS[5]) // San Francisco
  const [selectedTopic, setSelectedTopic] = useState<ResearchTopic | null>(null)
  const [isArticleHydrating, setIsArticleHydrating] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isResearchModalOpen, setIsResearchModalOpen] = useState(false)
  const [viewLayout, setViewLayout] = useState<ViewLayout>(() => {
    if (typeof window === 'undefined') return 'mosaic'
    const stored = window.localStorage.getItem(DESKTOP_LAYOUT_STORAGE_KEY)
    return stored === 'horizontal' ? 'horizontal' : 'mosaic'
  })
  const [mobileFeedLayout, setMobileFeedLayout] = useState<'vertical' | 'carousel'>(() => {
    if (typeof window === 'undefined') return 'vertical'
    const stored = window.localStorage.getItem(MOBILE_LAYOUT_STORAGE_KEY)
    return stored === 'carousel' ? 'carousel' : 'vertical'
  })
  const [audioState, setAudioState] = useState<AudioState>({
    isPlaying: false,
    currentTime: 0,
    duration: 0
  })
  const [showPodcastDrawer, setShowPodcastDrawer] = useState(false)
  const [podcastTopic, setPodcastTopic] = useState<ResearchTopic | null>(null)
  // State for intelligence reports with cockpit data
  const [intelligenceReportData, setIntelligenceReportData] = useState<IntelligenceReportData | null>(null)
  const [showCockpit, setShowCockpit] = useState(false)
  const { user } = useAuth()
  const hydratedSummaryIdsRef = useRef<Set<string>>(new Set())
  const isInitialLoadRef = useRef(true)

  const signalPendingTabFocus = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('research:showPending'))
    } catch (error) {
      console.warn('Failed to dispatch pending tab signal', error)
    }
  }, [])

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DESKTOP_LAYOUT_STORAGE_KEY, viewLayout)
    }
  }, [viewLayout])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(MOBILE_LAYOUT_STORAGE_KEY, mobileFeedLayout)
    }
  }, [mobileFeedLayout])

  const preloadTopicDetails = useCallback(async (topicBatch: ResearchTopic[]) => {
    for (const topic of topicBatch) {
      if (hydratedSummaryIdsRef.current.has(topic.id)) continue
      try {
        const fullDetails = await fetchFullDetails(parseInt(topic.id))
        if (!fullDetails) continue
        const detailSummary = fullDetails.results?.summary || fullDetails.summary || ''
        const detailQuery = fullDetails.results?.original_query || fullDetails.results?.query || fullDetails.query || topic.query || topic.title
        let cleanedSummary = resolveTopicPreviewSummary({
          summary: detailSummary,
          reportText: fullDetails.results?.report_text || '',
          query: detailQuery,
          title: topic.title
        })
        if (!cleanedSummary) {
          try {
            const reportText = await fetchResearchReport(parseInt(topic.id))
            cleanedSummary = resolveTopicPreviewSummary({
              summary: detailSummary,
              reportText,
              query: detailQuery,
              title: topic.title
            })
          } catch (reportError) {
            cleanedSummary = ''
            console.warn('Failed to derive summary from report for topic', topic.id, reportError)
          }
        }
        if (cleanedSummary) {
          hydratedSummaryIdsRef.current.add(topic.id)
        }
        setTopics(prev => prev.map(t => {
          if (t.id !== topic.id) return t
          return {
            ...t,
            summary: cleanedSummary || t.summary,
            description: cleanedSummary || t.description,
            query: detailQuery
          }
        }))
      } catch (error) {
        console.warn('Failed to hydrate summary for topic', topic.id, error)
      }
    }
  }, [])

  const mapExploreCardToTopic = useCallback((card: ResearchCard): ResearchTopic => {
    const authorId = (card as any).author_id
    const authorAvatarUrl =
      resolveAssetUrl(card.author_avatar || undefined, API_BASE_URL) ||
      buildAuthorAvatarImageUrl(authorId)

    const rawTitle = card.title || card.original_query || ''
    const normalizedTitle = normalizeResearchTitle(rawTitle)
    const title = normalizedTitle || rawTitle
    const previewSummary = resolveTopicPreviewSummary({
      summary: card.summary || '',
      reportText: '',
      query: card.original_query || rawTitle,
      title,
    })

    const imageUrl = resolveResearchCardImageUrl(card.image_url, API_BASE_URL)

    let category = card.category || 'General'
    if (category.toLowerCase() === 'research' || category.toLowerCase() === 'general') {
      const improvedCategory = api.getCategoryForTitle?.(card.title || '')
      if (improvedCategory && improvedCategory !== 'General') {
        category = improvedCategory
      }
    }

    return {
      id: card.id.toString(),
      title,
      description: previewSummary,
      summary: previewSummary,
      category,
      trending_percentage: Math.floor(Math.random() * 100),
      engagement_count: card.save_count || 0,
      like_count: card.like_count || 0,
      liked_by_user: Boolean(card.liked_by_user),
      sources: [],
      created_at: card.created_at,
      image_url: imageUrl,
      tags: [],
      report_text: '',
      key_findings: [],
      model: '',
      query: card.original_query || rawTitle,
      author: card.author_name ? {
        name: card.author_name,
        avatar_url: authorAvatarUrl,
        email: card.author_email || undefined
      } : undefined,
      // @ts-ignore augment at runtime
      author_id: authorId,
      is_intelligence_report: card.is_intelligence_report || false,
      intelligence_report_id: card.intelligence_report_id || undefined,
      podcast_status: card.podcast_status || undefined,
      podcast_audio_ready: Boolean(card.podcast_audio_ready),
      podcast_duration_seconds: card.podcast_duration_seconds || undefined,
      podcast_error_message: card.podcast_error_message || undefined,
      duration_seconds: card.podcast_duration_seconds || undefined,
    }
  }, [])

  const loadTopics = useCallback(async (options: { silent?: boolean } = {}) => {
    const { silent = false } = options
    try {
      const shouldShowSpinner = !silent && isInitialLoadRef.current
      if (shouldShowSpinner) {
        setIsLoading(true)
      }
      // console.log('🔍 Loading explore cards from optimized endpoint')
      
      // Load lightweight card data for fast initial load
      // Load fewer cards initially for faster time-to-first-paint; we'll prefetch next batch
      const response = await fetchExploreCards(30, 0)
      const cards = response.cards
      // console.log('📊 Explore cards loaded:', cards.length, 'items out of', response.total)
      
      // Convert cards to ResearchTopic format
      const topics = filterProductionDiscoveryItems(cards.map(mapExploreCardToTopic))
      
      // Sort topics by created_at date (newest first)
      topics.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        return dateB - dateA // Newest first
      })
      
      setTopics(prev => {
        const prevMap = new Map(prev.map(topic => [topic.id, topic]))
        return topics.map(topic => {
          const existing = prevMap.get(topic.id)
          if (!existing) return topic
          return {
            ...topic,
            summary: existing.summary || topic.summary,
            description: existing.description || topic.description,
            query: existing.query || topic.query,
            like_count: topic.like_count ?? existing.like_count,
            liked_by_user: typeof topic.liked_by_user === 'boolean' ? topic.liked_by_user : existing.liked_by_user
          }
        })
      })
      setLikeCounts(prev => {
        const updated = { ...prev }
        topics.forEach(topic => {
          updated[topic.id] = topic.like_count ?? 0
        })
        return updated
      })
      setLikedTopicIds(new Set(topics.filter(topic => topic.liked_by_user).map(topic => topic.id)))
      preloadTopicDetails(topics.slice(0, 8))
      // Preload next page asynchronously without blocking UI
      fetchExploreCards(30, 30).then(next => {
        const more = filterProductionDiscoveryItems(next.cards.map(mapExploreCardToTopic))
        
        // Sort the combined list by date (newest first) and remove duplicates by ID
        setTopics(prev => {
          const combined = [...prev, ...more]
          // Remove duplicates by ID
          const uniqueById = new Map()
          combined.forEach(topic => {
            if (!uniqueById.has(topic.id)) {
              uniqueById.set(topic.id, topic)
            }
          })
          const unique = Array.from(uniqueById.values())
          // Sort by date
          unique.sort((a, b) => {
            const dateA = new Date(a.created_at).getTime()
            const dateB = new Date(b.created_at).getTime()
            return dateB - dateA // Newest first
          })
          return unique
        })
        setLikeCounts(prev => {
          const updated = { ...prev }
          more.forEach(topic => {
            updated[topic.id] = topic.like_count ?? 0
          })
          return updated
        })
        setLikedTopicIds(prev => {
          const updated = new Set(prev)
          more.forEach(topic => {
            if (topic.liked_by_user) {
              updated.add(topic.id)
            }
          })
          return updated
        })
        preloadTopicDetails(more.slice(0, 6))
      }).catch(() => {})
      
    } catch (error) {
      console.error('Failed to load public research:', error)
      onShowToast({
        type: 'error',
        title: 'Failed to load content',
        description: 'Unable to load research articles. Please try again later.'
      })
      // Set empty array to show "No topics available" message
      setTopics([])
      analytics.trackError({
        error_type: 'explore_load_failed',
        error_message: error instanceof Error ? error.message : 'Failed to load explore content',
        page: 'explore',
      })
    } finally {
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false
        // Track explore page viewed on initial load
        analytics.trackPageViewed({ page: 'explore', referrer: document.referrer })
      }
      if (!silent) {
        setIsLoading(false)
      }
    }
  }, [mapExploreCardToTopic, onShowToast, preloadTopicDetails])

  // Load saved articles on mount and when page becomes visible
  useEffect(() => {
    const loadSavedArticles = async () => {
      try {
        const savedArticles = await fetchSavedResearch()
        const savedIds = new Set(savedArticles.map(article => article.research_id?.toString() || article.id?.toString()).filter(Boolean))
        setSavedTopicIds(savedIds)
        console.log('Loaded saved IDs:', Array.from(savedIds))
      } catch (error) {
        console.error('Failed to load saved articles:', error)
      }
    }
    
    loadSavedArticles()
    
    // Reload when page becomes visible (e.g., when switching tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadSavedArticles()
      }
    }
    
    // Listen for save/unsave events
    const handleSaveChange = () => {
      console.log('Save change detected on Explore page, reloading saved IDs...')
      loadSavedArticles()
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('saved-articles-changed', handleSaveChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('saved-articles-changed', handleSaveChange)
    }
  }, [])

  useEffect(() => {
    loadTopics()
  }, [loadTopics])

  useEffect(() => {
    topics.forEach((topic) => {
      const topicId = topic.id?.toString()
      if (!topicId || trackedTopicViewIdsRef.current.has(topicId)) return

      trackedTopicViewIdsRef.current.add(topicId)
      engagementTracking.trackCardViewed()
    })
  }, [engagementTracking, topics])

  // Background refresh every 5 minutes (300000 ms)
  useEffect(() => {
    const interval = setInterval(() => {
      loadTopics({ silent: true })
    }, 300000)
    return () => clearInterval(interval)
  }, [loadTopics])

  const handleTopicSelect = async (topic: ResearchTopic) => {
    if (!topic.id) {
      return
    }

    const topicId = topic.id.toString()
    
    // Track card click for engagement
    engagementTracking.trackCardClicked()
    engagementTracking.trackCategoryBrowsed(topic.category || 'General')
    
    // Track explore card click and research view with Amplitude
    const topicIndex = topics.findIndex(t => t.id === topic.id)
    analytics.trackExploreCardClicked({
      research_id: topicId,
      report_id: topic.intelligence_report_id || undefined,
      position: topicIndex >= 0 ? topicIndex : 0,
      category: topic.category,
      has_image: !!topic.image_url,
    })
    analytics.trackResearchViewed({
      research_id: topicId,
      report_id: topic.intelligence_report_id || undefined,
      source: 'explore',
      author_is_self: topic.author?.email === user?.email,
      category: topic.category,
    })

    const targetHash = `#explore/article/${topicId}`
    if (window.location.hash !== targetHash) {
      window.history.pushState({ tab: 'explore', modal: 'explore-article' }, '', targetHash)
    }

    // Check if this is an intelligence report
    const isIntelligenceReport = topic.is_intelligence_report || topic.model === 'intelligence'
    
    if (isIntelligenceReport && topic.intelligence_report_id) {
      // Fetch intelligence report data for cockpit
      setSelectedTopic(topic)
      setIsArticleHydrating(true)
      onArticleOpen?.(true)
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/intelligence/report/${topic.intelligence_report_id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          }
        })
        
        if (response.ok) {
          const reportData = await response.json()
          const hydratedPodcastState = hydratePodcastSurfaceState(topic, reportData)
          
          // Check if we have cockpit data (CIG and SIB)
          if (reportData.report_type === 'intelligence' && reportData.cig && reportData.sib) {
            console.log('[Explore] Intelligence report has cockpit data, loading cockpit view')
            const deepResearchPayload = reportData.deep_research || reportData.deepResearch || null
            setIntelligenceReportData({
              cig: reportData.cig,
              sib: reportData.sib,
              written: reportData.written,
              scenario: reportData.scenario,
              qa: reportData.qa,
              plan: reportData.plan,
              deepResearch: deepResearchPayload ? {
                text: deepResearchPayload.text || deepResearchPayload.content || '',
                sources: deepResearchPayload.sources || [],
                citations: deepResearchPayload.citations || [],
                status: deepResearchPayload.status,
                provider: deepResearchPayload.provider,
              } : null
            })
            setTopics((prev) => prev.map((candidate) => (
              candidate.id === topic.id
                ? { ...candidate, ...hydratedPodcastState }
                : candidate
            )))
            // Update topic with fetched data
            setSelectedTopic({
              ...topic,
              title: reportData.title || topic.title || topic.query || '',
              author: reportData.author_name ? {
                name: reportData.author_name,
                email: reportData.author_email,
                avatar_url: reportData.author_avatar
              } : topic.author,
              image_url: resolveAssetUrl(reportData.image_url, API_BASE_URL) || topic.image_url,
              ...hydratedPodcastState,
            })
            setShowCockpit(true)
            setIsArticleHydrating(false)
            return
          }
        }
      } catch (error) {
        console.error('[Explore] Error fetching intelligence report:', error)
      }
      setIsArticleHydrating(false)
    }
  }
  
  useEffect(() => {
    onArticleOpen?.(showCockpit)
  }, [showCockpit, onArticleOpen])

  const handleSaveTopic = async (topic: ResearchTopic) => {
    try {
      const topicId = topic.id.toString()
      
      // Check if already saved to toggle
      if (savedTopicIds.has(topicId)) {
        // Unsave the topic
        const success = await unsaveResearchArticle(parseInt(topic.id))
        
        if (success) {
          engagementTracking.trackUnsaved()
          analytics.trackArticleUnsaved(topicId, topic.intelligence_report_id || undefined)
          // Update saved state
          setSavedTopicIds(prev => {
            const newSet = new Set(prev)
            newSet.delete(topicId)
            return newSet
          })
          
          onShowToast({
            type: 'success',
            title: 'Removed',
            description: `"${topic.title}" has been removed from your collection.`
          })
        } else {
          onShowToast({
            type: 'error',
            title: 'Remove Failed',
            description: 'Unable to remove article. Please try again.'
          })
        }
      } else {
        // Save to backend
        const success = await saveResearchArticle(parseInt(topic.id))
        
        if (success) {
          engagementTracking.trackSaved()
          analytics.trackArticleSaved({
            research_id: topicId,
            report_id: topic.intelligence_report_id || undefined,
            source: 'explore',
            category: topic.category,
          })
          // Update saved state
          setSavedTopicIds(prev => {
            const newSet = new Set(prev)
            newSet.add(topicId)
            return newSet
          })
          
          onShowToast({
            type: 'success',
            title: 'Saved!',
            description: `"${topic.title}" has been saved to your collection.`
          })
        } else {
          // Check if it's an auth issue
          const token = localStorage.getItem('access_token')
          if (!token) {
            onShowToast({
              type: 'error',
              title: 'Not Logged In',
              description: 'Please log in to save articles to your collection.'
            })
          } else {
            onShowToast({
              type: 'error',
              title: 'Save Failed',
              description: 'Unable to save article. Please try again.'
            })
          }
        }
      }
    } catch (error) {
      console.error('Failed to save/unsave topic:', error)
      
      // Check if it's an auth issue
      const token = localStorage.getItem('access_token')
      if (!token) {
        onShowToast({
          type: 'error',
          title: 'Not Logged In',
          description: 'Please log in to save articles to your collection.'
        })
      } else {
        onShowToast({
          type: 'error',
          title: 'Operation Failed',
          description: 'Unable to complete the operation. Please ensure you are logged in and try again.'
        })
      }
    }
  }

  const handleToggleLike = async (topic: ResearchTopic) => {
    const topicId = topic.id.toString()
    if (pendingLikes.has(topicId)) {
      return
    }

    setPendingLikes(prev => {
      const updated = new Set(prev)
      updated.add(topicId)
      return updated
    })

    const isLiked = likedTopicIds.has(topicId)
    try {
      const response = isLiked
        ? await unlikeResearchArticle(parseInt(topic.id))
        : await likeResearchArticle(parseInt(topic.id))
      const nextCount = typeof response?.count === 'number'
        ? response.count
        : Math.max(0, (likeCounts[topicId] || 0) + (isLiked ? -1 : 1))

      setLikeCounts(prev => ({ ...prev, [topicId]: nextCount }))
      setLikedTopicIds(prev => {
        const updated = new Set(prev)
        if (isLiked) {
          updated.delete(topicId)
        } else {
          updated.add(topicId)
        }
        return updated
      })
      setTopics(prev => prev.map(t => (
        t.id === topic.id
          ? { ...t, like_count: nextCount, liked_by_user: !isLiked }
          : t
      )))
      if (isLiked) {
        analytics.trackArticleUnliked({
          research_id: topicId,
          report_id: topic.intelligence_report_id || undefined,
          source: 'explore',
        })
      } else {
        analytics.trackArticleLiked({
          research_id: topicId,
          report_id: topic.intelligence_report_id || undefined,
          source: 'explore',
        })
      }
    } catch (error) {
      console.error('Failed to toggle like:', error)
      const token = localStorage.getItem('access_token')
      onShowToast({
        type: 'error',
        title: token ? 'Unable to update like' : 'Login required',
        description: token ? 'Please try again in a moment.' : 'Sign in to like research articles.'
      })
    } finally {
      setPendingLikes(prev => {
        const updated = new Set(prev)
        updated.delete(topicId)
        return updated
      })
    }
  }

  const handlePlayAudio = async (topic: ResearchTopic) => {
    const reportId = topic.intelligence_report_id
    if (!topic.is_intelligence_report || !reportId) {
      return
    }

    const applyPodcastState = (next: typeof topic, podcastInfo: api.PodcastInfo) => ({
      ...next,
      podcast_status: (podcastInfo.audio_url ? 'ready' : podcastInfo.status) || next.podcast_status || 'missing',
      podcast_audio_ready: Boolean(podcastInfo.audio_url),
      podcast_duration_seconds: podcastInfo.duration_seconds ?? next.podcast_duration_seconds,
      podcast_error_message: podcastInfo.error_message ?? undefined,
      duration_seconds: podcastInfo.duration_seconds ?? next.duration_seconds,
      artwork_url: podcastInfo.artwork_url || next.artwork_url,
    })

    const updatePodcastState = (podcastInfo: api.PodcastInfo) => {
      setTopics((prev) => prev.map((candidate) => (
        candidate.id === topic.id ? applyPodcastState(candidate, podcastInfo) : candidate
      )))
      setSelectedTopic((prev) => (
        prev && prev.id === topic.id ? applyPodcastState(prev, podcastInfo) : prev
      ))
    }

    if (
      audioState.topic?.intelligence_report_id === reportId &&
      audioState.topic?.audio_url
    ) {
      const nextAudioState = {
        ...audioState,
        isPlaying: !audioState.isPlaying,
      }
      setAudioState(nextAudioState)
      onAudioStateChange(nextAudioState)
      return
    }

    try {
      let pendingToastShown = false
      const podcastInfo = await resolveIntelligencePodcastPlayback(reportId, {
        onPending: (pending) => {
          updatePodcastState(pending)
          if (pendingToastShown) return
          pendingToastShown = true
          onShowToast({
            type: 'info',
            title: 'Preparing Podcast',
            description: 'Podcast generation has started. Playback will begin as soon as audio is ready.',
          })
        },
      })
      updatePodcastState(podcastInfo)

      if (!podcastInfo.audio_url) {
        onShowToast({
          type: podcastInfo.status === 'failed' ? 'error' : 'warning',
          title: podcastInfo.status === 'failed' ? 'Podcast failed' : 'Podcast still processing',
          description:
            podcastInfo.error_message ||
            'The podcast is still being generated. Please try again in a moment.',
        })
        return
      }

      const newAudioState = buildPodcastAudioState(topic, podcastInfo)
      engagementTracking.trackPodcastPlayed()
      analytics.trackPodcastPlayed({
        research_id: topic.id.toString(),
        report_id: reportId,
        duration_listened_seconds: 0,
        completion_rate_percent: 0,
        source: 'article',
      })
      setSelectedTopic((prev) => (prev && prev.id === topic.id ? { ...newAudioState.topic! } : prev))
      setAudioState(newAudioState)
      onAudioStateChange(newAudioState)
    } catch (e) {
      console.error('Failed to create/play podcast:', e)
      onShowToast({
        type: 'error',
        title: 'Podcast failed',
        description: 'Unable to generate or play the podcast. Please try again later.'
      })
    }
  }



  const handleStartResearch = async (query: string, isPublic: boolean = false, _model: 'chatgpt' | 'gemini' | 'claude' = 'chatgpt') => {
    try {
      const pendingResearch = {
        query,
        depth: 'maximum',
        isPublic,
      }
      sessionStorage.setItem('autoStartResearch', JSON.stringify(pendingResearch))
      window.dispatchEvent(new CustomEvent('pendingResearchReady', { detail: pendingResearch }))

      onShowToast({
        type: 'success',
        title: 'Plan Ready to Review',
        description: `Review the research plan for "${query}" and approve it to begin.`
      })
      
      // Navigate to research page to see progress
      if (onTabChange) {
        onTabChange('research')
      }
      signalPendingTabFocus()
    } catch (error) {
      onShowToast({
        type: 'error',
        title: 'Research Failed',
        description: 'Failed to open the intelligence flow. Please try again.'
      })
    }
  }
  
  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      'Finance': 'bg-blue-600',
      'Healthcare': 'bg-green-600',
      'Technology': 'bg-purple-600',
      'History': 'bg-amber-600',
      'Environment': 'bg-emerald-600',
      'Politics': 'bg-red-600',
      'Business': 'bg-indigo-600',
      'Infrastructure': 'bg-gray-600',
      'Science': 'bg-cyan-600',
      'Culture': 'bg-pink-600',
      'General': 'bg-slate-600',
      'Education': 'bg-teal-600',
      'Sports': 'bg-orange-600',
      'Entertainment': 'bg-rose-600'
    }
    // Default to slate for general/unknown categories
    return colors[category] || 'bg-slate-600'
  }

  const getTopicLikeCount = (topicId: string): number => {
    if (likeCounts[topicId] !== undefined) {
      return likeCounts[topicId]
    }
    const topic = topics.find(t => t.id === topicId)
    return topic?.like_count ?? 0
  }

  const isTopicLiked = (topicId: string): boolean => likedTopicIds.has(topicId)

  const getTopicPreview = (topic: ResearchTopic): string => {
    return getTopicPreviewText(topic, PREVIEW_MAX_LENGTH)
  }

  const formatCreatedDate = (date: string): string => {
    const d = new Date(date)
    if (!Number.isFinite(d.getTime())) {
      return 'Recently created'
    }
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const renderActionRow = (topic: ResearchTopic, variant: 'light' | 'dark' = 'light') => {
    const isLiked = isTopicLiked(topic.id)
    const likeCount = getTopicLikeCount(topic.id)
    const podcastAction = getPodcastActionState(topic, audioState)
    const compactPodcastLabel = getCompactPodcastActionLabel(podcastAction.label)
    const baseButtonClasses =
      variant === 'dark'
        ? 'bg-white/15 hover:bg-white/25 text-white'
        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
    const readButtonClasses =
      variant === 'dark'
        ? 'bg-white/10 hover:bg-white/20 text-white'
        : 'bg-gray-900 text-white hover:bg-gray-800'
    const likeButtonClasses = isLiked
      ? variant === 'dark'
        ? 'bg-rose-500/80 hover:bg-rose-500 text-white'
        : 'bg-rose-500 hover:bg-rose-600 text-white'
      : baseButtonClasses
    const listenButtonClasses =
      podcastAction.status === 'playing'
        ? 'bg-blue-600 text-white hover:bg-blue-700'
        : podcastAction.status === 'failed'
          ? 'bg-amber-500 text-white hover:bg-amber-600'
          : `${baseButtonClasses} ${podcastAction.disabled ? 'opacity-70 cursor-not-allowed' : ''}`
    const disabledLike = pendingLikes.has(topic.id)

    const likeLabel = likeCount === 1 ? 'Like' : 'Likes'
    const actionRowClass = podcastAction.visible ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-2 gap-2'
    const actionButtonClass = 'min-w-0 min-h-[48px] flex items-center justify-center gap-1.5 rounded-2xl px-3 py-2.5 text-sm font-semibold leading-none transition-all active:scale-[0.98]'
    return (
      <div className={`w-full min-w-0 ${actionRowClass}`}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            handleToggleLike(topic)
          }}
          disabled={disabledLike}
          aria-pressed={isLiked}
          className={`${actionButtonClass} ${likeButtonClasses} ${disabledLike ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          <svg className="w-4 h-4" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          <span>{likeCount} {likeLabel}</span>
        </button>
        {podcastAction.visible && (
          <button
            type="button"
            onClick={(e) => handleListenAudio(e, topic)}
            disabled={podcastAction.disabled}
            className={`${actionButtonClass} ${listenButtonClasses}`}
            title={podcastAction.label}
            aria-label={podcastAction.label}
          >
            {podcastAction.showSpinner ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : podcastAction.status === 'playing' ? (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 4H6v16h4V4zm8 0h-4v16h4V4z"/>
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            )}
            <span className="truncate">{compactPodcastLabel}</span>
          </button>
        )}
        <button
          type="button"
          onClick={(e) => handleReadArticle(e, topic)}
          className={`${actionButtonClass} ${readButtonClasses}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          Read
        </button>
      </div>
    )
  }

  const renderDesktopHorizontalCard = (topic: ResearchTopic) => {
    const displayTitle = normalizeResearchTitle(topic.title)
    const createdLabel = formatCreatedDate(topic.created_at)
    const likeCount = getTopicLikeCount(topic.id)
    const isLiked = isTopicLiked(topic.id)
    const podcastAction = getPodcastActionState(topic, audioState)

    return (
      <div
        key={topic.id}
        data-card
        className="flex-shrink-0 cursor-pointer group relative transition-all duration-300 ease-out hover:scale-105 hover:z-50"
        style={{
          width: 'clamp(392px, 28vw, 560px)',
          height: 'clamp(600px, 34vw, 820px)',
          scrollSnapAlign: 'center'
        }}
        onClick={() => handleTopicSelect(topic)}
      >
        <div
          className="relative w-full h-full rounded-3xl overflow-hidden transition-all duration-300 group-hover:shadow-2xl"
          style={{
            boxShadow: `
              0 12px 48px rgba(0, 0, 0, 0.15),
              0 4px 24px rgba(0, 0, 0, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.1)
            `,
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          {topic.image_url ? (
            <>
              <img
                src={topic.image_url}
                alt={displayTitle}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900" />
          )}

          <div className="absolute inset-0 p-6 flex flex-col gap-4 justify-between">
            <div className="flex items-center justify-between">
              <span className="px-4 py-2 bg-white/15 backdrop-blur-xl border border-white/20 rounded-full text-white text-sm font-medium shadow-lg">
                {topic.category}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleSaveTopic(topic)
                }}
                className={`p-3 rounded-xl border backdrop-blur-sm transition ${
                  savedTopicIds.has(topic.id)
                    ? 'bg-white/25 text-white border-white/50'
                    : 'bg-black/30 text-white border-white/20 hover:bg-black/40'
                }`}
                title={savedTopicIds.has(topic.id) ? 'Remove from saved' : 'Save research'}
              >
                <svg className="w-5 h-5" fill={savedTopicIds.has(topic.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </button>
            </div>

            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-5 shadow-2xl">
              <h3 className="text-white text-xl font-bold leading-tight mb-4">
                {displayTitle}
              </h3>

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggleLike(topic)
                  }}
                  disabled={pendingLikes.has(topic.id)}
                  className={`flex items-center justify-center p-3 rounded-xl border transition ${
                    isLiked
                      ? 'bg-rose-500/80 border-rose-200 text-white'
                      : 'bg-white/15 border-white/30 text-white hover:bg-white/25'
                  } ${pendingLikes.has(topic.id) ? 'opacity-70 cursor-not-allowed' : ''}`}
                  title={isLiked ? 'Unlike' : 'Like'}
                >
                  <svg className="w-5 h-5" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>
                {podcastAction.visible && (
                  <button
                    onClick={(e) => handleListenAudio(e, topic)}
                    disabled={podcastAction.disabled}
                    className={`flex items-center justify-center p-3 rounded-xl border transition ${
                      podcastAction.status === 'playing'
                        ? 'bg-blue-500 text-white border-blue-300'
                        : podcastAction.status === 'failed'
                          ? 'bg-amber-500 text-white border-amber-300'
                          : 'bg-white/15 border-white/30 text-white hover:bg-white/25'
                    } ${podcastAction.disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
                    title={podcastAction.label}
                  >
                    {podcastAction.showSpinner ? (
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                    ) : podcastAction.status === 'playing' ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M10 4H6v16h4V4zm8 0h-4v16h4V4z"/>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </button>
                )}
                <button
                  onClick={(e) => handleReadArticle(e, topic)}
                  className="flex items-center justify-center p-3 rounded-xl border border-white/30 bg-white/15 text-white hover:bg-white/25 transition"
                  title="Read report"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </button>
              </div>
              <div className="flex items-center justify-between text-xs text-white/70 mt-3">
                <span>{likeCount} {likeCount === 1 ? 'Like' : 'Likes'}</span>
                <span>{createdLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderDesktopMosaicCard = (topic: ResearchTopic, index: number) => {
    const displayTitle = normalizeResearchTitle(topic.title)
    const likeCount = getTopicLikeCount(topic.id)
    const isLiked = isTopicLiked(topic.id)
    const podcastAction = getPodcastActionState(topic, audioState)
    const cardTypes = [
      { height: 'h-80', span: 'col-span-1' },
      { height: 'h-96', span: 'col-span-1' },
      { height: 'h-72', span: 'col-span-1' },
      { height: 'h-[28rem]', span: 'md:col-span-2 lg:col-span-1' },
      { height: 'h-64', span: 'col-span-1' },
      { height: 'h-[32rem]', span: 'md:col-span-2' },
    ]
    const cardType = cardTypes[index % cardTypes.length]

    return (
      <div
        key={topic.id}
        data-card
        className={`${cardType.span} ${cardType.height} cursor-pointer group relative transition-all duration-300 ease-out hover:scale-[1.02] hover:z-10`}
        onClick={(e) => {
          const target = e.target as HTMLElement
          if (target.closest('button')) {
            return
          }
          handleTopicSelect(topic)
        }}
      >
        <div className="relative w-full h-full rounded-3xl overflow-hidden transition-all duration-300 group-hover:shadow-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          {topic.image_url ? (
            <>
              <img
                src={topic.image_url}
                alt={displayTitle}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800" />
          )}
          
          <div className="absolute inset-0 p-6 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="px-4 py-2 bg-white/90 dark:bg-gray-900/90 text-gray-900 dark:text-white text-sm font-medium rounded-full backdrop-blur-sm">
                {topic.category}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleSaveTopic(topic)
                }}
                className={`p-2.5 rounded-xl backdrop-blur-sm ${
                  savedTopicIds.has(topic.id)
                    ? 'bg-white/90 text-gray-900'
                    : 'bg-white/20 hover:bg-white/30 text-white'
                }`}
                title={savedTopicIds.has(topic.id) ? 'Remove from saved' : 'Save'}
              >
                <svg className="w-5 h-5" fill={savedTopicIds.has(topic.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-3">
              <h3 className="text-white font-bold text-xl leading-tight">
                {displayTitle}
              </h3>
              
              <div className="flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggleLike(topic)
                  }}
                  disabled={pendingLikes.has(topic.id)}
                  className={`p-2.5 rounded-xl backdrop-blur-sm flex items-center gap-1 ${
                    isLiked
                      ? 'bg-rose-500/80 text-white'
                      : 'bg-white/20 hover:bg-white/30 text-white'
                  } ${pendingLikes.has(topic.id) ? 'opacity-70 cursor-not-allowed' : ''}`}
                  title={isLiked ? 'Unlike' : 'Like'}
                >
                  <svg className="w-5 h-5" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <span className="text-xs font-semibold">{likeCount}</span>
                </button>
                {podcastAction.visible && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleListenAudio(e, topic)
                    }}
                    disabled={podcastAction.disabled}
                    className={`p-2.5 rounded-xl backdrop-blur-sm ${
                      podcastAction.status === 'playing'
                        ? 'bg-blue-500 text-white'
                        : podcastAction.status === 'failed'
                          ? 'bg-amber-500 text-white'
                          : 'bg-white/20 hover:bg-white/30 text-white'
                    } ${podcastAction.disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
                    title={podcastAction.label}
                  >
                    {podcastAction.showSpinner ? (
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                    ) : podcastAction.status === 'playing' ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M10 4H6v16h4V4zm8 0h-4v16h4V4z"/>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleReadArticle(e, topic)
                  }}
                  className="p-2.5 rounded-xl backdrop-blur-sm bg-white/20 hover:bg-white/30 text-white"
                  title="Read report"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderSaveToggle = (topic: ResearchTopic, variant: 'light' | 'dark' = 'dark') => {
    const isSaved = savedTopicIds.has(topic.id)
    const baseClasses =
      variant === 'dark'
        ? 'bg-white/15 hover:bg-white/25 text-white border-white/20'
        : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-gray-200'
    const activeClasses =
      variant === 'dark'
        ? 'bg-green-500/40 text-white border-green-300/60'
        : 'bg-green-100 text-green-800 border-green-200'

    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          handleSaveTopic(topic)
        }}
        aria-pressed={isSaved}
        className={`p-2.5 rounded-xl transition-colors ${isSaved ? activeClasses : baseClasses}`}
        title={isSaved ? 'Remove from saved' : 'Save research'}
      >
        <svg className="w-5 h-5" fill={isSaved ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
      </button>
    )
  }

  const renderMobileCard = (topic: ResearchTopic, variant: 'vertical' | 'carousel' = 'vertical') => {
    const isCarousel = variant === 'carousel'
    const containerSpacing = isCarousel ? 'shrink-0 snap-start' : 'mx-4'
    const cardSpacing = isCarousel ? '' : 'mb-4'
    const cardPadding = isCarousel ? 'p-4' : 'p-5'
    const dropShadow = 'shadow-lg'
    const displayTitle = normalizeResearchTitle(topic.title)
    const previewText = getTopicPreview(topic)
    const cardHeight = 520
    const imageHeight = isCarousel ? 'clamp(12rem, 40vh, 22rem)' : `${Math.round(cardHeight * 0.75 * 0.55)}px`
    const titleClass = isCarousel
      ? 'min-w-0 flex-1 break-words text-lg font-bold leading-tight text-gray-900 dark:text-white line-clamp-3'
      : 'min-w-0 flex-1 break-words text-xl font-bold text-gray-900 dark:text-white'
    const previewClass = isCarousel
      ? 'break-words text-sm leading-relaxed text-gray-600 dark:text-gray-300 line-clamp-2'
      : 'break-words text-sm leading-relaxed text-gray-600 dark:text-gray-300 line-clamp-3'
    const headerSpacingClass = isCarousel ? 'space-y-2.5' : 'space-y-3'
    const metaSectionClass = isCarousel ? 'mt-auto flex flex-col gap-2.5' : 'mt-4 flex flex-col gap-3 h-full'
    const createdLabel = formatCreatedDate(topic.created_at)

    const authorAvatar =
      topic.author?.avatar_url ||
      buildAuthorAvatarImageUrl((topic as any).author_id)
    const authorInitialFallback = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(topic.author?.name || 'U')}`

    return (
      <div
        key={`${variant}-${topic.id}`}
        className={`${containerSpacing} ${cardSpacing} mobile-card-tap-target bg-white dark:bg-gray-800 rounded-3xl ${dropShadow} overflow-hidden cursor-pointer transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.99]`}
        style={{
          ...(isCarousel ? { height: 'calc(100dvh - 17rem)' } : { minHeight: `${cardHeight}px` }),
          ...(isCarousel
            ? {
                width: 'calc(100vw - 3rem)',
                maxWidth: 'calc(100vw - 3rem)'
              }
            : {})
        }}
        data-testid={`mobile-explore-card-${variant}`}
        onClick={() => handleTopicSelect(topic)}
      >
        <div className="flex flex-col h-full">
          <div className="relative w-full overflow-hidden" style={{ height: imageHeight }}>
            {topic.image_url ? (
              <img
                src={topic.image_url}
                alt={displayTitle}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute top-4 left-4">
              <span className={`px-4 py-2 ${getCategoryColor(topic.category)} text-white text-sm font-medium rounded-full`}>
                {topic.category}
              </span>
            </div>
          </div>

          <div className={`${cardPadding} flex-1 flex flex-col min-w-0`}>
            <div className={headerSpacingClass}>
              <div className="flex min-w-0 items-start gap-3">
                <h3 className={titleClass}>
                  {displayTitle}
                </h3>
                <div className="shrink-0 flex justify-end">
                  {renderSaveToggle(topic, 'light')}
                </div>
              </div>
              <p className={previewClass}>
                {previewText}
              </p>
            </div>

            <div className={metaSectionClass}>
              <div className="flex min-w-0 items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                <div className="h-9 w-9 shrink-0 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <img
                    src={authorAvatar || authorInitialFallback}
                    alt={topic.author?.name || 'Author avatar'}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = authorInitialFallback
                    }}
                  />
                </div>
                <div className="min-w-0 text-left">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                    {topic.author?.name || 'Insight Atlas'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {createdLabel}
                  </p>
                </div>
              </div>
              <div className="mt-auto">
                {renderActionRow(topic, 'light')}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handlePlayTrailer = (e: React.MouseEvent, topic: ResearchTopic) => {
    e.stopPropagation()
    // TODO: Implement trailer playback
    onShowToast({
      type: 'info',
      title: 'Coming Soon',
      description: 'Video trailers will be available soon!'
    })
  }

  const handleListenAudio = (e: React.MouseEvent, topic: ResearchTopic) => {
    e.stopPropagation()
    const podcastAction = getPodcastActionState(topic, audioState)
    if (!podcastAction.visible || podcastAction.disabled) {
      return
    }
    void handlePlayAudio(topic)
  }

  const handleReadArticle = (e: React.MouseEvent, topic: ResearchTopic) => {
    e.stopPropagation()
    handleTopicSelect(topic)
  }

  const selectedTopicPodcastAction = selectedTopic
    ? getPodcastActionState(selectedTopic, audioState)
    : null

  // Listen for global close-article signal via navigation or browser back
  useEffect(() => {
    const onCloseOverlay = () => {
      if (showCockpit) {
        setShowCockpit(false)
        setIntelligenceReportData(null)
        setSelectedTopic(null)
        onArticleOpen?.(false)
      }
    }
    window.addEventListener('insightatlas-close-article', onCloseOverlay as any)
    const onBrowserPop = () => onCloseOverlay()
    window.addEventListener('popstate', onBrowserPop)
    return () => {
      window.removeEventListener('insightatlas-close-article', onCloseOverlay as any)
      window.removeEventListener('popstate', onBrowserPop)
    }
  }, [showCockpit, onArticleOpen])

  // Add keyboard support for horizontal view
  useEffect(() => {
    if (viewMode === 'desktop' || viewMode === 'tablet') {
      if (viewLayout === 'horizontal') {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
          const carousel = document.querySelector('[data-carousel]') as HTMLElement
          if (carousel && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
            e.preventDefault()
            const scrollAmount = 400
            carousel.scrollBy({ 
              left: e.key === 'ArrowLeft' ? -scrollAmount : scrollAmount, 
              behavior: 'smooth' 
            })
          }
        }

        document.addEventListener('keydown', handleGlobalKeyDown)
        return () => document.removeEventListener('keydown', handleGlobalKeyDown)
      }
    }
  }, [viewMode, viewLayout])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
                          <div className="flex justify-center mb-4">
                  <LoadingAnimation size={48} />
                </div>
          <p className="text-gray-600 dark:text-gray-400">Loading trending topics...</p>
        </div>
      </div>
    )
  }

  // Desktop/Tablet View - Horizontal Card Deck
  if (viewMode === 'desktop' || viewMode === 'tablet') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-black overflow-x-hidden px-4 sm:px-6 lg:px-12 py-12">
        <div className="mx-auto max-w-6xl xl:max-w-7xl">
          {/* Header with View Toggle */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
            {/* App Title */}
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Explore
            </h1>
            
            {/* Controls */}
            <div className="flex items-center justify-end w-full sm:w-auto">
              {/* View Toggle */}
              <div className="flex items-center bg-white/10 dark:bg-white/5 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-2xl p-1 w-max sm:mr-24">
                <button
                  onClick={() => setViewLayout('horizontal')}
                  className={`p-2 rounded-xl transition-all duration-200 ${
                    viewLayout === 'horizontal' 
                      ? 'bg-black dark:bg-white text-white dark:text-black shadow-lg' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  title="Horizontal scroll view"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewLayout('mosaic')}
                  className={`p-2 rounded-xl transition-all duration-200 ${
                    viewLayout === 'mosaic' 
                      ? 'bg-black dark:bg-white text-white dark:text-black shadow-lg' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  title="Mosaic view"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          
          {topics.length > 0 ? (
            <div className="mt-8">
              {viewLayout === 'horizontal' ? (
                /* Horizontal Scrolling Card Deck */
                <div className="flex items-center justify-center min-h-[85vh] relative">
                  <div className="w-full max-w-full overflow-hidden">
                    <div 
                      className="flex items-center gap-6 overflow-x-auto scrollbar-hide pb-8 px-4"
                      data-carousel
                      style={{
                        scrollSnapType: 'x mandatory',
                        scrollBehavior: 'smooth'
                      }}
                      ref={(el) => {
                        if (el) {
                          let isScrolling = false
                          let velocity = 0
                          let lastX = 0
                          let lastTime = 0
                          let animationId: number

                          const momentum = () => {
                            if (Math.abs(velocity) > 0.1) {
                              el.scrollLeft += velocity
                              velocity *= 0.95 // Friction
                              animationId = requestAnimationFrame(momentum)
                            } else {
                              isScrolling = false
                            }
                          }

                          const handleMouseMove = (e: MouseEvent) => {
                            if (isScrolling) return
                            
                            const rect = el.getBoundingClientRect()
                            const mouseX = e.clientX - rect.left
                            const containerWidth = rect.width
                            const scrollableWidth = el.scrollWidth - containerWidth
                            
                            if (scrollableWidth > 0) {
                              const currentTime = performance.now()
                              
                              // Calculate target position
                              const scrollRatio = Math.max(0, Math.min(1, mouseX / containerWidth))
                              const targetScrollLeft = scrollRatio * scrollableWidth
                              
                              // Calculate velocity for momentum
                              if (lastTime > 0) {
                                const deltaTime = currentTime - lastTime
                                const deltaX = mouseX - lastX
                                velocity = (deltaX / deltaTime) * 8 // Reduced sensitivity
                              }
                              
                              // Smooth interpolation
                              const currentScrollLeft = el.scrollLeft
                              const diff = targetScrollLeft - currentScrollLeft
                              const step = diff * 0.05 // Smoother movement
                              
                              el.scrollLeft = currentScrollLeft + step
                              
                              lastX = mouseX
                              lastTime = currentTime
                            }
                          }

                          const handleKeyDown = (e: KeyboardEvent) => {
                            if (e.key === 'ArrowLeft') {
                              e.preventDefault()
                              el.scrollBy({ left: -400, behavior: 'smooth' })
                            } else if (e.key === 'ArrowRight') {
                              e.preventDefault()
                              el.scrollBy({ left: 400, behavior: 'smooth' })
                            }
                          }

                          const handleMouseEnter = () => {
                            isScrolling = false
                            velocity = 0
                            if (animationId) {
                              cancelAnimationFrame(animationId)
                            }
                            // Add keyboard listener when mouse enters
                            document.addEventListener('keydown', handleKeyDown)
                          }

                          const handleMouseLeave = () => {
                            if (Math.abs(velocity) > 1) {
                              isScrolling = true
                              momentum()
                            }
                            lastTime = 0
                            // Remove keyboard listener when mouse leaves
                            document.removeEventListener('keydown', handleKeyDown)
                          }

                          // Throttled mouse move for better performance
                          let rafId: number
                          const throttledMouseMove = (e: MouseEvent) => {
                            if (rafId) return
                            rafId = requestAnimationFrame(() => {
                              handleMouseMove(e)
                              rafId = 0
                            })
                          }

                          el.addEventListener('mouseenter', handleMouseEnter)
                          el.addEventListener('mousemove', throttledMouseMove)
                          el.addEventListener('mouseleave', handleMouseLeave)
                          
                          // Store cleanup function in a property instead of returning it
                          ;(el as any)._cleanup = () => {
                            el.removeEventListener('mouseenter', handleMouseEnter)
                            el.removeEventListener('mousemove', throttledMouseMove)
                            el.removeEventListener('mouseleave', handleMouseLeave)
                            document.removeEventListener('keydown', handleKeyDown)
                            
                            if (animationId) {
                              cancelAnimationFrame(animationId)
                            }
                            if (rafId) {
                              cancelAnimationFrame(rafId)
                            }
                          }
                        } else {
                          // Call cleanup when element is removed
                          if ((el as any)?._cleanup) {
                            ;(el as any)._cleanup()
                          }
                        }
                      }}
                    >
                      {topics.map(renderDesktopHorizontalCard)}
                    </div>
                  </div>
                </div>
              ) : (
                /* Mosaic Layout */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-max max-w-7xl mx-auto">
                  {topics.map((topic, index) => renderDesktopMosaicCard(topic, index))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                No topics available
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Check back later for the latest trending topics.
              </p>
            </div>
          )}
          
          {/* Scroll Hint - Moved to bottom of page for horizontal view */}
          {viewLayout === 'horizontal' && (
            <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full text-gray-600 dark:text-gray-400 text-sm shadow-lg">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                </svg>
                <span>Move mouse to scroll • Use ← → keys to navigate</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </div>
          )}
        </div>

        {/* Topic Drawer */}
        {/* Removed TopicDrawer */}

        {/* Research Modal */}
        <ResearchModal
          isOpen={isResearchModalOpen}
          onClose={() => setIsResearchModalOpen(false)}
          onStartResearch={handleStartResearch}
          viewMode={viewMode}
        />

        {/* Intelligence Cockpit View for intelligence reports (Desktop) */}
        {showCockpit && selectedTopic && intelligenceReportData && (() => {
          try {
            const cig = intelligenceReportData.cig || {}
            const cigMetadata = (cig as any).metadata || {}
            const scenarioPackage = intelligenceReportData.scenario
            const scenarioAxes = (() => {
              if (!scenarioPackage?.axis) return (cig as any).scenario_axes ?? []
              const axis = scenarioPackage.axis
              return [{
                axis_id: axis.axis_id || 'default',
                label: axis.label || 'Scenario Analysis',
                description: axis.description,
                states: (axis.states || []).map((state: any) => ({
                  state_id: state.state_id, label: state.label, short_description: state.short_description || state.description,
                  is_default: state.is_default, probability_band: state.probability_band,
                  triggers: (state.triggers || []).map((t: any) => ({ trigger_id: t.trigger_id || t.id, description: t.description, observability: t.observability, confidence: t.confidence })),
                  assumptions: (state.assumptions || []).map((a: any) => ({ assumption_id: a.assumption_id || a.id, text: a.text, rationale: a.rationale })),
                  metric_impacts: state.metric_impacts || [],
                })),
                triggers: (scenarioPackage.global_triggers || axis.triggers || []).map((t: any) => ({ id: t.trigger_id || t.id, label: t.label || t.description, definition: t.definition || t.description, indicator_type: t.indicator_type, maps_to_state_transitions: t.maps_to_state_transitions, confidence: t.confidence })),
                assumptions: (axis.assumptions || []).map((a: any) => ({ id: a.assumption_id || a.id, text: a.text, applies_to_state_id: a.applies_to_state_id })),
              }]
            })()
            const _sibTitle1 = intelligenceReportData?.sib?.research_title
            const _bestTitle1 = (_sibTitle1 && _sibTitle1.length > 10 && _sibTitle1 !== selectedTopic.query) ? _sibTitle1 : (cigMetadata.title && cigMetadata.title.length > 10 && cigMetadata.title !== selectedTopic.query) ? cigMetadata.title : cigMetadata.title || selectedTopic.title || selectedTopic.query
            const enhancedCig: CIG = { ...cig, metadata: { ...cigMetadata, schema_version: cigMetadata.schema_version || '2.2', report_id: cigMetadata.report_id || 'intelligence-report', title: _bestTitle1 }, sources: (cig as any).sources ?? [], claims: (cig as any).claims ?? [], entities: (cig as any).entities ?? [], metrics: normalizeLegacyMetrics((cig as any).metrics as any), relationships: (cig as any).relationships ?? [], figures: (cig as any).figures ?? [], datasets: (cig as any).datasets ?? [], watchlist: (cig as any).watchlist ?? [], key_findings: (cig as any).key_findings ?? [], executive_summary: (cig as any).executive_summary ?? [], bluf: ((cig as any).bluf ?? selectedTopic.title) || selectedTopic.query, report_sections: (cig as any).report_sections ?? [], scenario_axes: scenarioAxes.length > 0 ? scenarioAxes : (cig as any).scenario_axes }
            const selectedPersona = DEFAULT_PERSONAS.find(p => p.persona_id === 'general_exec') || DEFAULT_PERSONAS[0]
            const compiledViews = compileViews(
              enhancedCig,
              selectedPersona,
              intelligenceReportData.sib,
              intelligenceReportData.deepResearch ||
                (intelligenceReportData as any).deep_research ||
                (((intelligenceReportData as any).written_report || (intelligenceReportData as any).deep_research_report || (intelligenceReportData as any).report_text)
                  ? { text: (intelligenceReportData as any).written_report || (intelligenceReportData as any).deep_research_report || (intelligenceReportData as any).report_text }
                  : undefined)
            )
            const heroImageUrl = selectedTopic.image_url || (intelligenceReportData as any)?.image_url || (enhancedCig as any)?.metadata?.hero_image_url
            // Ensure researchId is a valid positive number
            const parsedResearchId = parseInt(selectedTopic.id)
            const validResearchId = !isNaN(parsedResearchId) && parsedResearchId > 0 ? parsedResearchId : undefined
            const studyMeta = { id: selectedTopic.intelligence_report_id ? `ia-${selectedTopic.intelligence_report_id}` : selectedTopic.id, intelligenceReportId: selectedTopic.intelligence_report_id, researchId: validResearchId, isSaved: savedTopicIds.has(selectedTopic.id), isPublic: selectedTopic.is_public ?? true, category: selectedTopic.category, model: selectedTopic.model, createdAt: selectedTopic.created_at, query: selectedTopic.query, researcher: selectedTopic.author?.name, researcherEmail: selectedTopic.author?.email, researcherAvatarUrl: selectedTopic.author?.avatar_url }
            if (!compiledViews) return null
            return (
              <div className="fixed inset-0 z-40 bg-gray-50 dark:bg-gray-900">
                <div className="h-full pl-24 overflow-y-auto">
                  <CockpitRuntime cig={enhancedCig} views={compiledViews} persona={selectedPersona} onBack={() => { setShowCockpit(false); setIntelligenceReportData(null); setSelectedTopic(null); onArticleOpen?.(false) }} onListen={selectedTopicPodcastAction?.visible && selectedTopic ? () => handlePlayAudio(selectedTopic) : undefined} listenLabel={selectedTopicPodcastAction?.label} listenDisabled={selectedTopicPodcastAction?.disabled} listenLoading={selectedTopicPodcastAction?.showSpinner} viewMode="desktop" hasFloatingAudio={Boolean(audioState.topic)} studyMeta={studyMeta} heroImageUrl={heroImageUrl} onNavigateToProfile={(userEmail) => { setShowCockpit(false); setIntelligenceReportData(null); setSelectedTopic(null); onArticleOpen?.(false); onNavigateToProfile?.(userEmail) }} />
                </div>
              </div>
            )
          } catch (error) { console.error('Error compiling cockpit views:', error); return null }
        })()}

      </div>
    )
  }

  // Mobile View - Feed Layout
  return (
    <div className="overflow-x-hidden bg-gray-50 dark:bg-gray-900">
      {/* Header with Research Button */}
      <PageHeader 
        title="Explore"
        action={
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-2xl p-1 shadow-inner">
            <button
              onClick={() => setMobileFeedLayout('vertical')}
              className={`p-2 rounded-xl transition-all duration-200 ${
                mobileFeedLayout === 'vertical'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                  : 'text-gray-500 dark:text-gray-300'
              }`}
              aria-pressed={mobileFeedLayout === 'vertical'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => setMobileFeedLayout('carousel')}
              className={`p-2 rounded-xl transition-all duration-200 ${
                mobileFeedLayout === 'carousel'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                  : 'text-gray-500 dark:text-gray-300'
              }`}
              aria-pressed={mobileFeedLayout === 'carousel'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h7v7H4zM13 6h7v7h-7zM4 15h7v7H4zM13 15h7v7h-7z" />
              </svg>
            </button>
          </div>
        }
      />

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
                                  <div className="flex justify-center mb-4">
                        <LoadingAnimation size={48} />
                      </div>
            <p className="text-gray-600 dark:text-gray-400">Loading insights...</p>
          </div>
        </div>
      ) : topics.length === 0 ? (
        <div className="flex items-center justify-center h-96 px-8">
          <div className="text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No research available</h3>
            <p className="text-gray-600 dark:text-gray-400">Start a new research to see insights here</p>
          </div>
        </div>
      ) : (
        /* Feed View for Mobile */
        <>
          {mobileFeedLayout === 'carousel' ? (
            <div
              className="overflow-hidden"
              data-testid="mobile-explore-layout"
              data-layout="horizontal-carousel"
            >
              <div
                className="mobile-card-scroll-track flex gap-4 overflow-x-auto snap-x pl-4 pr-5 py-3 scrollbar-hide"
                style={{
                  scrollPaddingInlineStart: '1rem',
                  scrollPaddingInlineEnd: '1.25rem'
                }}
                data-testid="mobile-explore-carousel-track"
              >
                {topics.map(topic => renderMobileCard(topic, 'carousel'))}
              </div>
              <p className="px-4 text-xs text-gray-500 dark:text-gray-400">
                Swipe horizontally to browse featured research.
              </p>
            </div>
          ) : (
            <div
              className=""
              data-testid="mobile-explore-layout"
              data-layout="vertical-list"
            >
              {topics.map(topic => renderMobileCard(topic))}
            </div>
          )}
        </>
      )}

      {/* Research Modal */}
      <ResearchModal
        isOpen={isResearchModalOpen}
        onClose={() => setIsResearchModalOpen(false)}
        onStartResearch={handleStartResearch}
        viewMode={viewMode}
      />

      {/* Intelligence Cockpit View for intelligence reports (Mobile) */}
      {showCockpit && selectedTopic && intelligenceReportData && (() => {
        try {
          const cig = intelligenceReportData.cig || {}
          const cigMetadata = (cig as any).metadata || {}
          const scenarioPackage = intelligenceReportData.scenario
          const scenarioAxes = (() => {
            if (!scenarioPackage?.axis) return (cig as any).scenario_axes ?? []
            const axis = scenarioPackage.axis
            return [{ axis_id: axis.axis_id || 'default', label: axis.label || 'Scenario Analysis', description: axis.description, states: (axis.states || []).map((state: any) => ({ state_id: state.state_id, label: state.label, short_description: state.short_description || state.description, is_default: state.is_default, probability_band: state.probability_band, triggers: (state.triggers || []).map((t: any) => ({ trigger_id: t.trigger_id || t.id, description: t.description, observability: t.observability, confidence: t.confidence })), assumptions: (state.assumptions || []).map((a: any) => ({ assumption_id: a.assumption_id || a.id, text: a.text, rationale: a.rationale })), metric_impacts: state.metric_impacts || [] })), triggers: (scenarioPackage.global_triggers || axis.triggers || []).map((t: any) => ({ id: t.trigger_id || t.id, label: t.label || t.description, definition: t.definition || t.description, indicator_type: t.indicator_type, maps_to_state_transitions: t.maps_to_state_transitions, confidence: t.confidence })), assumptions: (axis.assumptions || []).map((a: any) => ({ id: a.assumption_id || a.id, text: a.text, applies_to_state_id: a.applies_to_state_id })) }]
          })()
          const _sibTitle2 = intelligenceReportData?.sib?.research_title
          const _bestTitle2 = (_sibTitle2 && _sibTitle2.length > 10 && _sibTitle2 !== selectedTopic.query) ? _sibTitle2 : (cigMetadata.title && cigMetadata.title.length > 10 && cigMetadata.title !== selectedTopic.query) ? cigMetadata.title : cigMetadata.title || selectedTopic.title || selectedTopic.query
          const enhancedCig: CIG = { ...cig, metadata: { ...cigMetadata, schema_version: cigMetadata.schema_version || '2.2', report_id: cigMetadata.report_id || 'intelligence-report', title: _bestTitle2 }, sources: (cig as any).sources ?? [], claims: (cig as any).claims ?? [], entities: (cig as any).entities ?? [], metrics: normalizeLegacyMetrics((cig as any).metrics as any), relationships: (cig as any).relationships ?? [], figures: (cig as any).figures ?? [], datasets: (cig as any).datasets ?? [], watchlist: (cig as any).watchlist ?? [], key_findings: (cig as any).key_findings ?? [], executive_summary: (cig as any).executive_summary ?? [], bluf: ((cig as any).bluf ?? selectedTopic.title) || selectedTopic.query, report_sections: (cig as any).report_sections ?? [], scenario_axes: scenarioAxes.length > 0 ? scenarioAxes : (cig as any).scenario_axes }
          const selectedPersona = DEFAULT_PERSONAS.find(p => p.persona_id === 'general_exec') || DEFAULT_PERSONAS[0]
          const compiledViews = compileViews(
            enhancedCig,
            selectedPersona,
            intelligenceReportData.sib,
            intelligenceReportData.deepResearch ||
              (intelligenceReportData as any).deep_research ||
              (((intelligenceReportData as any).written_report || (intelligenceReportData as any).deep_research_report || (intelligenceReportData as any).report_text)
                ? { text: (intelligenceReportData as any).written_report || (intelligenceReportData as any).deep_research_report || (intelligenceReportData as any).report_text }
                : undefined)
          )
          const heroImageUrl = selectedTopic.image_url || (intelligenceReportData as any)?.image_url || (enhancedCig as any)?.metadata?.hero_image_url
          // Ensure researchId is a valid positive number (mobile view)
          const parsedResearchIdMobile = parseInt(selectedTopic.id)
          const validResearchIdMobile = !isNaN(parsedResearchIdMobile) && parsedResearchIdMobile > 0 ? parsedResearchIdMobile : undefined
          const studyMeta = { id: selectedTopic.intelligence_report_id ? `ia-${selectedTopic.intelligence_report_id}` : selectedTopic.id, intelligenceReportId: selectedTopic.intelligence_report_id, researchId: validResearchIdMobile, isSaved: savedTopicIds.has(selectedTopic.id), isPublic: selectedTopic.is_public ?? true, category: selectedTopic.category, model: selectedTopic.model, createdAt: selectedTopic.created_at, query: selectedTopic.query, researcher: selectedTopic.author?.name, researcherEmail: selectedTopic.author?.email, researcherAvatarUrl: selectedTopic.author?.avatar_url }
          if (!compiledViews) return null
          return (
            <div className="fixed inset-0 z-40 bg-gray-50 dark:bg-gray-900">
              <div className="h-full overflow-y-auto">
                <CockpitRuntime cig={enhancedCig} views={compiledViews} persona={selectedPersona} onBack={() => { setShowCockpit(false); setIntelligenceReportData(null); setSelectedTopic(null); onArticleOpen?.(false) }} onListen={selectedTopicPodcastAction?.visible && selectedTopic ? () => handlePlayAudio(selectedTopic) : undefined} listenLabel={selectedTopicPodcastAction?.label} listenDisabled={selectedTopicPodcastAction?.disabled} listenLoading={selectedTopicPodcastAction?.showSpinner} viewMode="mobile" hasFloatingAudio={Boolean(audioState.topic)} studyMeta={studyMeta} heroImageUrl={heroImageUrl} onNavigateToProfile={(userEmail) => { setShowCockpit(false); setIntelligenceReportData(null); setSelectedTopic(null); onArticleOpen?.(false); onNavigateToProfile?.(userEmail) }} />
              </div>
            </div>
          )
        } catch (error) { console.error('Error compiling cockpit views:', error); return null }
      })()}

    </div>
  )
} 